import {
  ArrowType,
  LineType,
  MetamodelAggregatedElement,
  MetamodelAssetFolder,
  MetamodelComponentType,
  MetamodelReferenceType,
  MetamodelWorkspace,
} from '@ardoq/api-types';
import {
  SimpleBlockDiagramContentType,
  SimpleBlockDiagramEdge,
  SimpleBlockDiagramNode,
  arrowMap,
  lineTypeMap,
} from '@ardoq/simple-block-diagram';
import { ArdoqIconCategory, IconName, getIcon } from '@ardoq/icons';
import { colors } from '@ardoq/design-tokens';

const getLabel = ({ name, metadata }: MetamodelAggregatedElement) =>
  `${name} (${metadata.instanceCount})`;

const getComponentTypeLabelColor = ({ metadata }: MetamodelComponentType) =>
  metadata.instanceCount ? colors.grey950 : colors.grey500;

const getComponentTypeColor = ({ style, metadata }: MetamodelComponentType) =>
  metadata.instanceCount ? style.color || colors.green600 : colors.white950;

const WORKSPACE_FOLDER_ICON = {
  id: IconName.OPENED_FOLDER,
  label: 'Folder',
  category: ArdoqIconCategory.Ardoq,
  representation: 'none',
  isSVG: true,
};

const WORKSPACE_ICON = {
  id: IconName.WORKSPACE,
  label: 'Workspace',
  isSVG: true,
  category: ArdoqIconCategory.Ardoq,
  representation: 'none',
};

const getComponentTypeIcon = ({ style }: MetamodelComponentType) =>
  getIcon(style.icon);

const getComponentTypeParentId = ({
  parent,
  workspaceId,
}: MetamodelComponentType) => (parent ? parent : workspaceId);

const getReferenceTypeColor = ({ style }: MetamodelReferenceType) =>
  style.color || colors.grey900;

const getReferenceTypeLine = ({ style }: MetamodelReferenceType) =>
  (style && style.line && lineTypeMap[style.line]) || LineType.SOLID;

const getReferenceTypeLineBeginning = ({ style }: MetamodelReferenceType) =>
  (style && style.lineBeginning && arrowMap.get(style.lineBeginning)) ||
  ArrowType.NONE;

const getReferenceTypeLineEnding = ({ style }: MetamodelReferenceType) =>
  (style && style.lineEnding && arrowMap.get(style.lineEnding)) ||
  ArrowType.DEFAULT;

export const createAssetFolderNode = (
  folder: MetamodelAssetFolder
): SimpleBlockDiagramNode => ({
  id: folder.id,
  componentId: folder.id,
  label: folder.name,
  color: colors.grey400,
  parentId: undefined,
  type: SimpleBlockDiagramContentType.WORKSPACE_FOLDER,
  representationData: {
    isImage: false,
    value: '',
    icon: WORKSPACE_FOLDER_ICON,
  },
});

export const createWorkspaceNode = (
  workspace: MetamodelWorkspace
): SimpleBlockDiagramNode => ({
  id: workspace.id,
  componentId: workspace.id,
  label: workspace.name,
  color: colors.grey400,
  parentId: workspace.folder ? workspace.folder : undefined,
  type: SimpleBlockDiagramContentType.WORKSPACE,
  representationData: {
    isImage: false,
    value: '',
    icon: WORKSPACE_ICON,
  },
});

const SHOW_COMPONENT_TYPE_ICONS = false;

export const createComponentNode = (
  metamodelComponentType: MetamodelComponentType
): SimpleBlockDiagramNode => {
  const iconRepresentationData = getComponentTypeIcon(metamodelComponentType);
  return {
    id: metamodelComponentType.id,
    componentId: metamodelComponentType.id,
    color: getComponentTypeColor(metamodelComponentType),
    label: getLabel(metamodelComponentType),
    labelColor: getComponentTypeLabelColor(metamodelComponentType),
    parentId: getComponentTypeParentId(metamodelComponentType),
    type: SimpleBlockDiagramContentType.COMPONENT,
    representationData: SHOW_COMPONENT_TYPE_ICONS
      ? {
          isImage: false,
          value: iconRepresentationData.id,
          icon: iconRepresentationData,
        }
      : null,
  };
};

export const createReferenceEdge = (
  metamodelReferenceType: MetamodelReferenceType
): SimpleBlockDiagramEdge => ({
  id: metamodelReferenceType.id,
  referenceId: metamodelReferenceType.id,
  sourceId: metamodelReferenceType.source,
  targetId: metamodelReferenceType.target,
  color: getReferenceTypeColor(metamodelReferenceType),
  line: getReferenceTypeLine(metamodelReferenceType),
  lineBeginning: getReferenceTypeLineBeginning(metamodelReferenceType),
  lineEnding: getReferenceTypeLineEnding(metamodelReferenceType),
  label: getLabel(metamodelReferenceType),
  type: SimpleBlockDiagramContentType.REFERENCE,
});
